import { storyblokEditable } from "@storyblok/react";
import Image from "next/image";
import Link from "next/link";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Container } from "../layout/container";
import Typography from "../ui/typography";
import { RenderRichtext } from "../RenderRichtext";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { ContentSectionStoryblok } from "@/types/sb-component-types";
import { resolveSbLink } from "@/lib/storyblok/resolve-sb-link";
import { useCurrentLocale } from "@/locales/client";
export const ContentSection = ({
  blok
}: {
  blok: ContentSectionStoryblok;
}) => {
  const locale = useCurrentLocale();
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    amount: 0.3
  });
  return <motion.section ref={ref} {...storyblokEditable(blok)} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 50
  }} initial={{
    opacity: 0,
    y: 50
  }} transition={{
    duration: 0.5
  }} data-sentry-element="unknown" data-sentry-component="ContentSection" data-sentry-source-file="ContentSection.tsx">
      <Container className={cn("group rounded-md py-16", blok.background === "light" ? "bg-white" : "bg-slate-100")} data-sentry-element="Container" data-sentry-source-file="ContentSection.tsx">
        <div className={cn("flex flex-col gap-12", blok.layout === "image-right" && "md:flex-row", blok.layout === "center" && "items-center text-center md:flex-col", blok.layout === "image-left" && "md:flex-row-reverse")}>
          <motion.div animate={isInView ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} className={cn("flex flex-col justify-center", blok.layout !== "center" && "md:w-1/2")} initial={{
          opacity: 0,
          y: 20
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} data-sentry-element="unknown" data-sentry-source-file="ContentSection.tsx">
            <Typography className="mb-4 text-4xl font-bold" variant="h2" data-sentry-element="Typography" data-sentry-source-file="ContentSection.tsx">
              {blok.title}
            </Typography>
            <RenderRichtext content={blok.content} data-sentry-element="RenderRichtext" data-sentry-source-file="ContentSection.tsx" />
            {blok.cta_text && blok.cta_link && <motion.div animate={isInView ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 10
          }} initial={{
            opacity: 0,
            y: 10
          }} transition={{
            duration: 0.3,
            delay: 0.4
          }}>
                <Button asChild className="mt-4 w-fit" size="lg">
                  <Link href={resolveSbLink(blok.cta_link, locale)}>
                    {blok.cta_text}
                  </Link>
                </Button>
              </motion.div>}
          </motion.div>
          {blok.image?.filename && blok.layout !== "center" && <motion.div animate={isInView ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} className={cn("overflow-hidden rounded-lg md:w-1/2")} initial={{
          opacity: 0,
          y: 20
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }}>
              <Image alt={blok.image.alt || blok.title} className="h-auto w-full rounded-lg shadow-lg transition-transform duration-500 group-hover:scale-105" height={300} src={blok.image.filename} width={500} />
            </motion.div>}
        </div>
      </Container>
    </motion.section>;
};